import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardService } from '../../dashboard.service';
import { AuthService } from 'src/app/services/auth.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import {
    DocumentRedactionFlowData,
    DocumentRedactionFlowNode,
    REDACTION_TYPES,
    REDACTION_STATUSES
} from './document-redaction-flow-dialog.interface';
import { RedactionStepEditDialogComponent, RedactionStepEditData } from './redaction-step-edit-dialog/redaction-step-edit-dialog.component';

@Component({
    selector: 'app-document-redaction-flow-dialog',
    templateUrl: './document-redaction-flow-dialog.component.html',
    styleUrls: ['./document-redaction-flow-dialog.component.scss']
})
export class DocumentRedactionFlowDialogComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    flowData: DocumentRedactionFlowData | null = null;
    isLoading = true;
    isSaving = false;
    isExporting = false;
    errorMessage: string | null = null;

    private basePath = environment.interop.basePath;

    redactionTypes = REDACTION_TYPES;
    redactionStatuses = REDACTION_STATUSES;

    // Cached data for edit dialog
    availableFunctii: any[] = [];
    availableDirectii: any[] = [];
    dataLoaded = false;

    // Permission slugs
    readonly PERM_PROCESS = 'indoc-module.registratura_documents.generabile.redaction-process';
    readonly PERM_REJECT = 'indoc-module.registratura_documents.generabile.redaction-reject';
    readonly PERM_REORDER = 'indoc-module.registratura_documents.generabile.redaction-reorder';
    readonly PERM_SKIP = 'indoc-module.registratura_documents.generabile.redaction-skip';
    readonly PERM_MANAGE = 'indoc-module.registratura_documents.generabile.redaction-manage';

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            documentGeneratId: number;
            templateName?: string;
            canEdit: boolean;
            // User context for permission checks
            currentUserId?: number;
            activeFunctieId?: number;
            activeDirectieId?: number;
            activeCompartimentId?: number;
        },
        private dialogRef: MatDialogRef<DocumentRedactionFlowDialogComponent>,
        private dialog: MatDialog,
        private dashboardService: DashboardService,
        private authService: AuthService,
        public permissionsService: PermissionsService
    ) {}

    ngOnInit(): void {
        this.loadFlowData();
        this.loadAvailableData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadAvailableData(): void {
        forkJoin({
            functii: this.dashboardService.getAvailableFunctii(),
            directii: this.dashboardService.getAvailableDirectii()
        }).pipe(takeUntil(this.destroy$))
        .subscribe({
            next: (response) => {
                this.availableFunctii = response.functii?.data || response.functii || [];
                this.availableDirectii = response.directii?.data || response.directii || [];
                this.dataLoaded = true;
            },
            error: (error) => {
                console.error('Error loading available data:', error);
                // Continue anyway, user can still view the flow
                this.dataLoaded = true;
            }
        });
    }

    loadFlowData(): void {
        this.isLoading = true;
        this.errorMessage = null;

        this.dashboardService.getDocumentRedactionFlow(this.data.documentGeneratId).subscribe({
            next: (response: any) => {
                if (response && response.data) {
                    this.flowData = response.data;
                }
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading redaction flow:', error);
                this.errorMessage = 'Nu s-a putut incarca fluxul de redactare.';
                this.isLoading = false;
            }
        });
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    // Drag and drop pentru reordonare
    onDrop(event: CdkDragDrop<DocumentRedactionFlowNode[]>): void {
        if (!this.flowData?.can_reorder) return;

        const pendingItems = this.getPendingRedactions();
        if (event.previousIndex === event.currentIndex) return;

        moveItemInArray(pendingItems, event.previousIndex, event.currentIndex);

        // Recalculate orders starting from minimum pending order
        const minOrder = Math.min(...pendingItems.map(p => p.order));
        pendingItems.forEach((item, index) => {
            item.order = minOrder + index;
        });

        this.saveNewOrder();
    }

    getPendingRedactions(): DocumentRedactionFlowNode[] {
        return this.flowData?.redactions.filter(r => r.type === 0) || [];
    }

    getProcessedRedactions(): DocumentRedactionFlowNode[] {
        return this.flowData?.redactions.filter(r => r.type !== 0) || [];
    }

    saveNewOrder(): void {
        if (!this.flowData) return;

        this.isSaving = true;
        const orderIds = this.getPendingRedactions().map(r => r.id);

        this.dashboardService.reorderDocumentRedactions(
            this.data.documentGeneratId,
            orderIds
        ).subscribe({
            next: () => {
                this.isSaving = false;
            },
            error: (error) => {
                console.error('Error saving order:', error);
                this.isSaving = false;
                this.loadFlowData(); // Reload to reset
            }
        });
    }

    // Process current step
    processCurrentStep(): void {
        if (!this.flowData?.can_process) return;

        const currentRedaction = this.flowData.redactions.find(
            r => r.order === this.flowData?.current_order
        );

        if (!currentRedaction) return;
        this.processAndAdvance(currentRedaction);
    }

    /**
     * Process current step AND automatically advance to next (or complete circuit)
     * This is the main action button - combines process + advance in one step
     */
    processAndAdvance(node: DocumentRedactionFlowNode): void {
        const isEditUpload = node.redaction_type === 1;

        Swal.fire({
            title: isEditUpload ? 'Finalizare si Trimitere' : 'Aprobare si Trimitere',
            html: `
                <p><strong>Pas:</strong> ${node.functie_name || 'N/A'}</p>
                <p><strong>Tip:</strong> ${isEditUpload ? 'Editare + Upload' : 'Review/Aprobare'}</p>
                <hr>
                <p>Dupa confirmare, documentul va fi trimis automat la urmatorul operator din circuit.</p>
            `,
            input: 'textarea',
            inputLabel: 'Observatii (optional)',
            inputPlaceholder: 'Introduceti observatii...',
            showCancelButton: true,
            confirmButtonText: isEditUpload ? 'Finalizeaza si Trimite' : 'Aproba si Trimite',
            cancelButtonText: 'Anuleaza',
            confirmButtonColor: '#28a745'
        }).then((result) => {
            if (result.isConfirmed) {
                this.isSaving = true;
                this.dashboardService.processRedactionStep(
                    this.data.documentGeneratId,
                    node.id,
                    { observations: result.value || null }
                ).subscribe({
                    next: (response) => {
                        this.isSaving = false;
                        if (response.data?.is_complete) {
                            Swal.fire({
                                title: 'Redactare Finalizata!',
                                text: 'Circuitul de redactare s-a finalizat. Documentul trece la circuitul de semnare.',
                                icon: 'success'
                            }).then(() => {
                                this.dialogRef.close({ refreshDocument: true });
                            });
                        } else {
                            Swal.fire('Succes!', response.data?.message || 'Pasul a fost procesat si trimis la urmatorul operator.', 'success');
                            this.loadFlowData();
                        }
                    },
                    error: (error) => {
                        this.isSaving = false;
                        Swal.fire('Eroare', error.error?.message || 'Nu s-a putut procesa pasul.', 'error');
                    }
                });
            }
        });
    }

    rejectStep(redaction: DocumentRedactionFlowNode): void {
        Swal.fire({
            title: 'Respingere pas redactare',
            html: `
                <p><strong>Pas:</strong> ${redaction.functie_name || 'N/A'}</p>
                <p>Pasul va fi respins si trimis inapoi la operatorul anterior.</p>
            `,
            input: 'textarea',
            inputLabel: 'Motiv respingere (obligatoriu)',
            inputPlaceholder: 'Introduceti motivul...',
            inputValidator: (value) => {
                if (!value || value.trim().length < 5) {
                    return 'Motivul trebuie sa aiba minim 5 caractere';
                }
                return null;
            },
            showCancelButton: true,
            confirmButtonText: 'Respinge',
            cancelButtonText: 'Anuleaza',
            confirmButtonColor: '#dc3545'
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                this.isSaving = true;
                this.dashboardService.rejectRedactionStep(
                    this.data.documentGeneratId,
                    redaction.id,
                    { observations: result.value }
                ).subscribe({
                    next: (response) => {
                        this.isSaving = false;
                        Swal.fire({
                            title: 'Respins!',
                            text: response.data?.message || 'Pasul a fost respins si trimis inapoi.',
                            icon: 'success'
                        });
                        this.loadFlowData();
                    },
                    error: (error) => {
                        this.isSaving = false;
                        Swal.fire('Eroare', error.error?.message || 'Nu s-a putut respinge pasul.', 'error');
                    }
                });
            }
        });
    }

    sendToNext(): void {
        Swal.fire({
            title: 'Trimite la urmatorul',
            text: 'Confirmati trimiterea documentului la urmatorul operator din circuit?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Trimite',
            cancelButtonText: 'Anuleaza',
            confirmButtonColor: '#007bff'
        }).then((result) => {
            if (result.isConfirmed) {
                this.dashboardService.sendToNextRedactor(this.data.documentGeneratId).subscribe({
                    next: (response) => {
                        if (response.data?.is_complete) {
                            Swal.fire('Finalizat!', 'Circuitul de redactare s-a finalizat. Documentul trece la semnare.', 'success');
                            this.dialogRef.close({ refreshDocument: true });
                        } else {
                            Swal.fire('Succes!', 'Documentul a fost trimis la urmatorul operator.', 'success');
                            this.loadFlowData();
                        }
                    },
                    error: (error) => {
                        Swal.fire('Eroare', error.error?.message || 'Nu s-a putut trimite documentul.', 'error');
                    }
                });
            }
        });
    }

    // Helper methods
    getTypeIcon(type: number): string {
        return this.redactionTypes[type]?.icon || 'edit_document';
    }

    getTypeLabel(type: number): string {
        return this.redactionTypes[type]?.label || 'Editare';
    }

    getTypeClass(type: number): string {
        return this.redactionTypes[type]?.class || 'redaction-type-edit';
    }

    getStatusIcon(status: number): string {
        return this.redactionStatuses[status]?.icon || 'schedule';
    }

    getStatusLabel(status: number): string {
        return this.redactionStatuses[status]?.label || 'In asteptare';
    }

    getStatusClass(status: number): string {
        return this.redactionStatuses[status]?.class || 'status-pending';
    }

    formatDate(dateString: string | null): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('ro-RO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    get hasRedactions(): boolean {
        return !!(this.flowData?.redactions && this.flowData.redactions.length > 0);
    }

    get isCurrentUsersTurn(): boolean {
        return this.flowData?.can_process || false;
    }

    get canReorder(): boolean {
        return (this.flowData?.can_reorder || false) && this.hasPermission(this.PERM_REORDER);
    }

    get canProcess(): boolean {
        return this.hasPermission(this.PERM_PROCESS);
    }

    get canReject(): boolean {
        return this.hasPermission(this.PERM_REJECT);
    }

    get canSkip(): boolean {
        return this.hasPermission(this.PERM_SKIP);
    }

    hasPermission(slug: string): boolean {
        return this.permissionsService.hasPermission(slug);
    }

    get isRedactionComplete(): boolean {
        return this.flowData?.redaction_step === 'complete';
    }

    get isRedactionInProgress(): boolean {
        return this.flowData?.redaction_step === 'in_progress' || this.flowData?.redaction_step === 'pending';
    }

    getCurrentRedaction(): DocumentRedactionFlowNode | undefined {
        if (!this.flowData) return undefined;
        return this.flowData.redactions.find(r => r.order === this.flowData?.current_order);
    }

    isCurrentStep(node: DocumentRedactionFlowNode): boolean {
        return node.order === this.flowData?.current_order;
    }

    /**
     * Verifică dacă utilizatorul curent este alocat pe acest nod
     * Logica: specific_user_id match OR (functie_id match AND directie+compartiment match)
     */
    isUserAssignedToNode(node: DocumentRedactionFlowNode): boolean {
        // Dacă e asignat un user specific, verificăm user_id
        if (node.specific_user_id) {
            return node.specific_user_id === this.data.currentUserId;
        }

        // Altfel verificăm functie_id + directie_id + compartiment_id
        const functieMatch = node.user_type === this.data.activeFunctieId;
        const directieMatch = !node.directie_id || node.directie_id === this.data.activeDirectieId;
        const compartimentMatch = !node.compartiment_id || node.compartiment_id === this.data.activeCompartimentId;

        return functieMatch && directieMatch && compartimentMatch;
    }

    /**
     * Verifică dacă utilizatorul poate procesa pasul curent (combinație locală + backend)
     */
    canUserProcessCurrentStep(): boolean {
        const currentNode = this.getCurrentRedaction();
        if (!currentNode) return false;

        // Backend spune can_process SAU e manager
        const backendAllows = this.flowData?.can_process || this.canManage;

        // Verificare locală: e alocat pe nod
        const isAssigned = this.isUserAssignedToNode(currentNode);

        return backendAllows && (isAssigned || this.canManage);
    }

    /**
     * Verifică dacă un pas poate fi resetat (rollback) - doar pașii done/rejected recenți
     */
    canResetNode(node: DocumentRedactionFlowNode): boolean {
        // Doar admin/manage poate face reset
        if (!this.canManage) return false;

        // Doar pașii done sau rejected pot fi resetați
        if (node.type !== 1 && node.type !== 2) return false; // 1=done, 2=rejected

        // Nu poate reseta dacă există pași ulteriori procesați
        const laterProcessedSteps = this.flowData?.redactions.filter(
            r => r.order > node.order && (r.type === 1 || r.type === 2)
        ) || [];

        return laterProcessedSteps.length === 0;
    }

    /**
     * Reset/Rollback un pas procesat la pending
     */
    resetStep(node: DocumentRedactionFlowNode): void {
        Swal.fire({
            title: 'Reset Pas Redactare',
            html: `
                <p>Sunteti sigur ca doriti sa resetati acest pas?</p>
                <p><strong>Pas:</strong> ${node.functie_name || 'N/A'}</p>
                <p><strong>Status curent:</strong> ${node.type === 1 ? 'Finalizat' : 'Respins'}</p>
                <hr>
                <p class="text-warning">Pasul va reveni la status "In asteptare" si va trebui procesat din nou.</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Da, reseteaza',
            cancelButtonText: 'Anuleaza',
            confirmButtonColor: '#f59e0b'
        }).then((result) => {
            if (result.isConfirmed) {
                this.isSaving = true;
                this.dashboardService.resetRedactionStep(
                    this.data.documentGeneratId,
                    node.id
                ).subscribe({
                    next: (response) => {
                        this.isSaving = false;
                        Swal.fire('Resetat!', response.data?.message || 'Pasul a fost resetat cu succes.', 'success');
                        this.loadFlowData();
                    },
                    error: (error) => {
                        this.isSaving = false;
                        Swal.fire('Eroare', error.error?.message || 'Nu s-a putut reseta pasul.', 'error');
                    }
                });
            }
        });
    }

    get canManage(): boolean {
        return this.hasPermission(this.PERM_MANAGE);
    }

    // Edit a pending redaction step
    editRedactionStep(node: DocumentRedactionFlowNode): void {
        const dialogData: RedactionStepEditData = {
            mode: 'edit',
            documentGeneratId: this.data.documentGeneratId,
            redactionId: node.id,
            currentData: {
                redaction_type: node.redaction_type,
                user_type: node.user_type,
                functie_name: node.functie_name,
                directie_id: node.directie_id,
                directie_name: node.directie_name,
                compartiment_id: node.compartiment_id,
                compartiment_name: node.compartiment_name,
                specific_user_id: node.specific_user_id,
                specific_user_name: node.specific_user_name
            },
            functii: this.availableFunctii,
            directii: this.availableDirectii
        };

        const dialogRef = this.dialog.open(RedactionStepEditDialogComponent, {
            width: '550px',
            maxHeight: '90vh',
            data: dialogData,
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.success) {
                this.loadFlowData();
            }
        });
    }

    // Add a new redaction step
    addRedactionStep(afterNode?: DocumentRedactionFlowNode): void {
        const dialogData: RedactionStepEditData = {
            mode: 'add',
            documentGeneratId: this.data.documentGeneratId,
            afterOrder: afterNode?.order,
            functii: this.availableFunctii,
            directii: this.availableDirectii
        };

        const dialogRef = this.dialog.open(RedactionStepEditDialogComponent, {
            width: '550px',
            maxHeight: '90vh',
            data: dialogData,
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.success) {
                this.loadFlowData();
            }
        });
    }

    // Remove a pending redaction step
    removeRedactionStep(node: DocumentRedactionFlowNode): void {
        Swal.fire({
            title: 'Confirmare ștergere',
            html: `
                <p>Sigur doriți să ștergeți acest pas de redactare?</p>
                <p><strong>Funcție:</strong> ${node.functie_name || 'N/A'}</p>
                <p><strong>Tip:</strong> ${this.getTypeLabel(node.redaction_type)}</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Șterge',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#dc3545'
        }).then((result) => {
            if (result.isConfirmed) {
                this.isSaving = true;
                this.dashboardService.removeRedactionStep(
                    this.data.documentGeneratId,
                    node.id
                ).subscribe({
                    next: (response) => {
                        this.isSaving = false;
                        if (response.success) {
                            Swal.fire('Succes!', 'Pasul a fost șters.', 'success');
                            this.loadFlowData();
                        } else {
                            Swal.fire('Eroare', response.message || 'Nu s-a putut șterge pasul.', 'error');
                        }
                    },
                    error: (error) => {
                        this.isSaving = false;
                        Swal.fire('Eroare', error.error?.message || 'Nu s-a putut șterge pasul.', 'error');
                    }
                });
            }
        });
    }

    // Check if node can be edited (only pending and not current)
    canEditNode(node: DocumentRedactionFlowNode): boolean {
        return node.type === 0 && this.canManage && this.data.canEdit;
    }

    // Check if node can be removed (only pending and not current step)
    canRemoveNode(node: DocumentRedactionFlowNode): boolean {
        return node.type === 0 && !this.isCurrentStep(node) && this.canManage && this.data.canEdit;
    }

    /**
     * Export redaction flow to PDF
     */
    async exportToPdf(): Promise<void> {
        if (this.isExporting) return;

        this.isExporting = true;

        try {
            const pdfUrl = this.basePath + 'api/registratura/document/generabile/redactions/flow/' + this.data.documentGeneratId + '/pdf';
            const token = await this.authService.getToken();

            const response = await fetch(pdfUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/pdf'
                }
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `circuit-redactare-doc-${this.data.documentGeneratId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            Swal.fire('Eroare', 'Nu s-a putut genera PDF-ul.', 'error');
        } finally {
            this.isExporting = false;
        }
    }
}
